import { Mark, Student } from '@/plugins/api'
import alert from '@/plugins/alert'
import { get, max, orderBy, keyBy } from 'lodash'
import moment from 'moment'
import { getFactorsByFilter } from './utils'

const getMaxColForFactors = (totalMarks = 0, defaultColNum = 0) => {
  if (totalMarks >= defaultColNum) return { maxCol: totalMarks + 2, isOverDefault: true }
  return { maxCol: defaultColNum, isOverDefault: false }
}

const setMaxColForFactors = ({ factors = [], marks = [], students = [] }) => {
  const tmpFactors = keyBy(factors, 'id')
  Object.values(tmpFactors).reduce((_, factor) => {
    const factorMarks = students.reduce((acc, student) => {
      const markNo = marks.filter(m => get(m, 'factor.id') === factor.id && get(m, 'student.id') === student.id).length
      return [...acc, markNo]
    }, [])
    tmpFactors[factor.id] = {
      ...tmpFactors[factor.id],
      ...getMaxColForFactors(max(factorMarks), factor.quantity)
    }
  }, [])
  return tmpFactors
}

const getDateToFetch = () => {
  const dateStart = moment().startOf('day')
  const dateEnd = moment().endOf('day')
  return { dateStart, dateEnd }
}

export default {
  namespaced: true,
  state: {
    marks: [],
    students: [],
    studentMarks: {},
    filterParam: {},
    semesterMaxCol: -1,
    factors: [],
    isClassExtraCurriculum: false,
    // import excel
    excelFactors: [],
    excelData: [],
    excelIsCreated: false,
    excelFileName: ''
  },
  actions: {
    resetExcelImportState({ commit }) {
      commit('changeState', {
        excelFactors: [],
        excelData: [],
        excelIsCreated: false,
        excelFileName: ''
      })
    },
    saveExcelImportPreview({ commit }, params) {
      commit('changeState', params)
    },
    async saveImportMarks(_, data) {
      try {
        const mark = await Mark.createMultiple(data)
        return mark
      } catch (e) {
        alert.error(e)
      }
    },
    async fetchMarks({ commit }, options) {
      try {
        const { dateStart, dateEnd } = getDateToFetch()

        let factors = getFactorsByFilter(options.subject.factors, options.semester.type)
        const isClassExtraCurriculum = get(options, 'class.type') === 'extraCurriculum' // EC
        let classesDictForEC = {}
        if (isClassExtraCurriculum) classesDictForEC = get(options, 'class.metadata.coreOrders', {})

        const markFetchParams = {
          class: get(options, 'class.id'),
          subject: get(options, 'subject.id'),
          semester: get(options, 'semester.id'),
          _limit: -1,
          _sort: 'date:ASC'
        }
        const marks = await Mark.fetch(markFetchParams)

        const collator = new Intl.Collator('vi')
        let tmp = get(options, 'class.students', [])
        if (!tmp.length) {
          tmp = await Student.fetch({ currentClass: get(options, 'class.id') })
        }
        let students = tmp
          .filter(s => s.status === 'active')
          .map(s => {
            const fn = s.formatedName.split(' ').shift()
            const ln = s.formatedName.replace(`${fn} `, '')
            return { ...s, fn, ln }
          })
          .sort((a, b) => {
            const r = collator.compare(a.fn, b.fn)
            return r !== 0 ? r : collator.compare(a.ln, b.ln)
          })
        if (isClassExtraCurriculum) {
          students = orderBy(students, ['currentClass', 'asc'])
        }

        // lấy số cột điểm tối đa trên 1 factor
        factors = setMaxColForFactors({ factors, marks, students })

        const factorsInArr = orderBy(Object.values(factors), ['multiply'])
        const studentMarks = students.map(student => {
          let tmpMarks = []
          factorsInArr.forEach(factor => {
            let tmp = marks
              .filter(m => get(m, 'student.id') === student.id && get(m, 'factor.id') === factor.id)
              .map(m => ({
                ...m,
                rawValue: m.value,
                markInDate: m.date === dateStart.toISOString() || moment(m.date).isBetween(dateStart, dateEnd)
              }))
            tmp = Array.from(Array(factor.maxCol).keys()).map(
              index => tmp[index] || { index, value: undefined, markInDate: true, factor }
            )
            tmpMarks = tmpMarks.concat(tmp)
          })
          if (isClassExtraCurriculum) student.currentClassTitle = classesDictForEC[student.currentClass]
          return {
            student,
            marks: tmpMarks
          }
        })

        commit('changeState', {
          studentMarks,
          filterParam: options,
          factors,
          isClassExtraCurriculum
        })
      } catch (error) {
        console.error(error)
        alert.error(error)
      }
    },
    async createMark({ commit }, data) {
      try {
        const mark = await Mark.create(data)
        commit('createMark', mark)
        return mark
      } catch (e) {
        alert.error(e)
      }
    },
    async removeMark({ commit }, id) {
      await Mark.remove(id)
      commit('removeMark', id)
    },
    async updateMarks({ dispatch }, items) {
      await Promise.all(items.map(item => dispatch('updateMark', item)))
    },
    async updateMark({ commit }, { id, ...mark }) {
      const m = await Mark.update(id, mark)
      commit('updateMark', m)
    },
    setStudents({ commit }, students) {
      commit('setStudents', orderBy(students, ['nameToNumber']))
    },
    setFactor({ commit }, factor) {
      commit('setFactor', factor)
    }
  },
  mutations: {
    setMarks(state, marks) {
      state.marks = marks
    },
    setStudents(state, students) {
      state.students = students
    },
    updateMark(state, mark) {},
    createMark(state, mark) {},
    removeMark(state, id) {},
    setFactor(state, factor) {
      state.factor = factor
    }
  },
  getters: {
    studentMarks: state => {
      return Object.values(state.studentMarks)
    }
  }
}
